import type { CaseStudyMetric } from "@/types/marketing"

export type VanCleefCopy = {
  relationship: string
  context: string
  problem: { eyebrow: string; heading: string; body: string }
  system: { label: string; nodes: string[] }
  workflow: {
    eyebrow: string
    heading: string
    items: Array<{ title: string; body: string }>
  }
  evidence: {
    eyebrow: string
    heading: string
    introduction: string
    metrics: CaseStudyMetric[]
    implementationLabel: string
    implementation: string[]
  }
  media: { label: string; alt: string; caption: string }
  limits: { eyebrow: string; heading: string; body: string }
  final: { eyebrow: string; heading: string; body: string; action: string }
}

export const VANCLEEF_COPY: Record<"hr" | "en", VanCleefCopy> = {
  hr: {
    relationship: "Klijentski rad · Privatna operativna platforma",
    context:
      "VanCleef je hrvatski računovodstveni ured koji vodi turističke i smještajne klijente. Zmile je osmislio i izgradio sustav koji povezuje postojeće izvore podataka, automatizaciju i ljudsku kontrolu u jednom radnom prostoru.",
    problem: {
      eyebrow: "Poslovno ograničenje",
      heading: "Četiri sustava. Mnogo ručnih prijenosa. Premalo zajedničkog konteksta.",
      body:
        "Podaci o turistima, rezervacijama, vlasničkim obračunima i računima dolazili su iz odvojenih tokova. Timu je trebalo kontrolirano mjesto za povezivanje boravaka, provjeru iznimki, pripremu obračuna i praćenje automatiziranog rada.",
    },
    system: {
      label: "Povezani operativni sustav",
      nodes: ["eVisitor", "Booking.com izvozi", "Novasol dokumenti", "e-računi"],
    },
    workflow: {
      eyebrow: "Što je izgrađeno",
      heading: "Automatizacija radi ponavljajući dio. Ljudi zadržavaju pregled i odluku.",
      items: [
        {
          title: "Turisti i boravci",
          body: "Dnevni eVisitor uvoz, grupiranje turista, izračun noćenja i obavijesti za završene boravke.",
        },
        {
          title: "Povezivanje rezervacija",
          body: "Booking.com tablice povezuju se s boravcima, dok nejasna podudaranja i razlike ostaju u stanju za pregled.",
        },
        {
          title: "Dokumenti i obračuni",
          body: "Novasol dokumenti preuzimaju se, čitaju i povezuju bez prepisivanja već potvrđenih ili ručno unesenih podataka.",
        },
        {
          title: "Priprema računa",
          body: "Sustav priprema kupca, stavke smještaja i turističke pristojbe, PDV te korekcije za razlike od jednog centa.",
        },
        {
          title: "Vidljiv rad u pozadini",
          body: "Statusi, ponovni pokušaji, tragovi i dijagnostički zapisi čine neuspjeli posao vidljivim i provjerljivim.",
        },
      ],
    },
    evidence: {
      eyebrow: "Dokazi iz implementacije",
      heading: "Brojevi su označeni prema onome što stvarno dokazuju.",
      introduction:
        "Dvije vrijednosti izravno su izmjerene u implementaciji. Procjena vremena koristi vidljivu formulu i nije predstavljena kao potvrđena produkcijska ušteda.",
      metrics: [
        {
          kind: "measured",
          value: "4 povezana sustava",
          label: "eVisitor, e-računi, Novasol i Booking.com podaci u jednom operativnom radnom prostoru.",
        },
        {
          kind: "capacity",
          value: "Do 300 autentikacija",
          label: "Upravljani kapacitet iz jedne validirane eVisitor TAN liste.",
        },
        {
          kind: "estimate",
          value: "67–125 sati / 500 računa",
          label: "Procijenjena ručna priprema koja se može izbjeći; kontrola i iznimke ostaju ljudima.",
          basis: "500 računa × 8–15 minuta izbjegnute pripreme ÷ 60.",
        },
      ],
      implementationLabel: "Provjerena implementacija · 30. srpnja 2026.",
      implementation: [
        "13 produktnih stranica",
        "22 podatkovna modela",
        "5 vrsta pozadinske automatizacije",
        "67 deklariranih automatiziranih testova",
        "55/55 provjerenih jediničnih testova",
      ],
    },
    media: {
      label: "Javno siguran produkcijski prikaz",
      alt: "VanCleef prijava u privatnu aplikaciju za automatizaciju računovodstvenih procesa.",
      caption:
        "Produkcijska prijava potvrđuje privatni, interno provisioniran pristup. Autentificirani podaci klijenata i turista nisu javno prikazani.",
    },
    limits: {
      eyebrow: "Granice dokaza",
      heading: "Procjena nije isto što i potvrđen rezultat.",
      body:
        "Ne objavljujemo postotke smanjenja pogrešaka, potvrđene sate uštede, rast prihoda ni zadovoljstvo zaposlenika bez produkcijskih podataka i odobrenja klijenta. Booking.com je tok uvoza datoteka, ne integracija u stvarnom vremenu, a ljudski pregled ostaje namjeran dio procesa.",
    },
    final: {
      eyebrow: "Sličan problem u vašem poslovanju?",
      heading: "Počnimo procesom koji danas traži najviše ručnog prijenosa.",
      body: "U besplatnom 30-minutnom razgovoru provjerit ćemo postoji li koristan i izvediv prvi korak.",
      action: "Zatražite besplatne 30-minutne konzultacije",
    },
  },
  en: {
    relationship: "Client work · Private operations platform",
    context:
      "VanCleef is a Croatian accounting office serving tourism and accommodation clients. Zmile designed and built a system that brings existing data sources, automation, and human control into one operational workspace.",
    problem: {
      eyebrow: "Business constraint",
      heading: "Four systems. Repeated handoffs. Too little shared context.",
      body:
        "Tourist records, reservations, owner statements, and invoices arrived through separate flows. The team needed one controlled place to match stays, review exceptions, prepare charges, and see what background work had completed or failed.",
    },
    system: {
      label: "Connected operations system",
      nodes: ["eVisitor", "Booking.com exports", "Novasol documents", "e-računi"],
    },
    workflow: {
      eyebrow: "What was built",
      heading: "Automation handles repetition. People keep review and responsibility.",
      items: [
        {
          title: "Tourists and stays",
          body: "Daily eVisitor imports, tourist grouping, night calculations, and notifications for completed stays.",
        },
        {
          title: "Reservation matching",
          body: "Booking.com spreadsheets match reservations to stays while ambiguous matches and differences remain visible for review.",
        },
        {
          title: "Documents and charges",
          body: "Novasol documents are retrieved, parsed, and matched without overwriting confirmed or manually entered information.",
        },
        {
          title: "Invoice preparation",
          body: "The system prepares customers, accommodation and tourist-tax lines, VAT, and one-cent reconciliation before invoice creation.",
        },
        {
          title: "Visible background work",
          body: "Statuses, retries, traces, and diagnostics make failed automation understandable and actionable.",
        },
      ],
    },
    evidence: {
      eyebrow: "Implementation evidence",
      heading: "Every number is labeled by what it actually proves.",
      introduction:
        "Two values are measured directly from the implementation. The time range is a code-derived estimate with a visible formula, not claimed production savings.",
      metrics: [
        {
          kind: "measured",
          value: "4 systems connected",
          label: "eVisitor, e-računi, Novasol, and Booking.com data in one operational workspace.",
        },
        {
          kind: "capacity",
          value: "Up to 300 authentications",
          label: "Managed capacity from one validated eVisitor TAN-list upload.",
        },
        {
          kind: "estimate",
          value: "67–125 hours / 500 invoices",
          label: "Estimated manual preparation avoided; review and exceptions remain with the team.",
          basis: "500 invoices × 8–15 minutes of avoided preparation ÷ 60.",
        },
      ],
      implementationLabel: "Verified implementation · 30 July 2026",
      implementation: [
        "13 product pages",
        "22 database models",
        "5 background automation types",
        "67 declared automated tests",
        "55/55 verified unit tests",
      ],
    },
    media: {
      label: "Public-safe production view",
      alt: "VanCleef sign-in page for the private accounting-process automation application.",
      caption:
        "The production sign-in confirms private, internally provisioned access. Authenticated client and tourist data is not shown publicly.",
    },
    limits: {
      eyebrow: "Evidence limits",
      heading: "An estimate is not a confirmed outcome.",
      body:
        "We do not publish error-reduction percentages, confirmed hours saved, revenue growth, or employee-satisfaction claims without production data and client approval. Booking.com is a file-import workflow rather than a real-time API, and human review remains a deliberate part of the system.",
    },
    final: {
      eyebrow: "A similar constraint in your business?",
      heading: "Start with the process that creates the most repeated handoffs today.",
      body: "In a free 30-minute conversation, we will determine whether there is a useful and feasible first step.",
      action: "Request a free 30-minute consultation",
    },
  },
}
